export const createPdfLayout = (
	doc,
	{ logo, footer, marginLeft = 10, contentWidth = 190, footerY = 285, startY = 20 }
) => {
	const defaultFontSize = 12;
	const SIGNATURES_HEIGHT = 65;
	const SIGNATURES_GAP_BEFORE = 22;
	const SIGNATURES_BLOCK_HEIGHT = SIGNATURES_HEIGHT + SIGNATURES_GAP_BEFORE;
	const contentBottom = footerY - 2;
	let yPos = startY;
	let pageStarted = false;

	const remainingSpace = () => contentBottom - yPos;

	const measureLinesHeight = (lines, fontSize = defaultFontSize, gapAfter = 4) => {
		doc.setFontSize(fontSize);
		const { h } = doc.getTextDimensions(lines);
		return h + gapAfter;
	};

	const addLogo = () => {
		doc.addImage(logo, "PNG", 3, 3, 25, 10);
	};

	const addFooter = () => {
		doc.addImage(footer, "PNG", 80, footerY, 45, 10);
	};

	const newPage = () => {
		if (pageStarted) addFooter();
		doc.addPage();
		pageStarted = true;
		addLogo();
		yPos = startY;
	};

	const ensureSpace = (height) => {
		if (yPos + height > contentBottom) {
			newPage();
		}
	};

	const ensureKeepTogether = (height) => {
		if (remainingSpace() < height) {
			newPage();
		}
	};

	const measureText = (text, { width = contentWidth, fontSize = defaultFontSize, gapAfter = 4 } = {}) => {
		doc.setFontSize(fontSize);
		const lines = doc.splitTextToSize(text, width);
		return measureLinesHeight(lines, fontSize, gapAfter);
	};

	const writeParagraph = (text, options = {}) => {
		const {
			indent = 0,
			gapAfter = 4,
			width = contentWidth,
			fontSize = defaultFontSize,
			bold = false,
			keepWithNext = 0,
		} = options;
		doc.setFontSize(fontSize);
		doc.setFont("arial", bold ? "bold" : "normal");
		const lines = doc.splitTextToSize(text, width - indent);
		const height = measureLinesHeight(lines, fontSize, gapAfter);
		ensureSpace(height + keepWithNext);
		doc.text(lines, marginLeft + indent, yPos);
		yPos += height;
	};

	const writeLine = (text, options = {}) => {
		const { indent = 0, fontSize = defaultFontSize, bold = false, gapAfter = 6 } = options;
		doc.setFontSize(fontSize);
		doc.setFont("arial", bold ? "bold" : "normal");
		const height = measureLinesHeight([text], fontSize, gapAfter);
		ensureSpace(height);
		doc.text(text, marginLeft + indent, yPos);
		yPos += height;
	};

	const writeRichLine = (parts, { indent = 0, gapAfter = 4 } = {}) => {
		doc.setFontSize(defaultFontSize);
		const height = measureLinesHeight([parts.map((p) => p.text).join("")], defaultFontSize, gapAfter);
		ensureSpace(height);
		let x = marginLeft + indent;
		for (const part of parts) {
			doc.setFont("arial", part.bold ? "bold" : "normal");
			doc.text(part.text, x, yPos);
			x += doc.getTextWidth(part.text);
		}
		yPos += height;
	};

	const writeSectionTitle = (title, lineEnd, indent = 0) => {
		doc.setFontSize(defaultFontSize);
		doc.setFont("arial", "bold");
		ensureSpace(8);
		doc.text(title, marginLeft + indent, yPos);
		doc.line(marginLeft + indent, yPos + 1, lineEnd, yPos + 1);
		yPos += 7;
	};

	const writeMainTitle = (lines) => {
		doc.setFont("arial", "bold");
		doc.setFontSize(20);
		for (const { text, x } of lines) {
			doc.text(text, x, yPos);
			yPos += 10;
		}
		yPos += 4;
		doc.setFontSize(defaultFontSize);
		doc.setFont("arial", "normal");
	};

	const renderPlanBox = (planName, plan) => {
		const itemLineHeight = 5;
		const headerHeight = 20;
		const paddingBottom = 8;
		const boxHeight = headerHeight + plan.items.length * itemLineHeight + paddingBottom;

		ensureSpace(boxHeight + 4);

		const boxY = yPos;

		doc.setDrawColor(0);
		doc.setFillColor(235, 235, 235);
		doc.rect(marginLeft, boxY, contentWidth, boxHeight, "F");

		doc.setFillColor(105, 105, 105);
		doc.rect(marginLeft, boxY, plan.headerWidth, headerHeight, "F");

		doc.setTextColor(255, 255, 255);
		doc.setFontSize(30);
		doc.setFont("arial", "normal");
		doc.text(planName, marginLeft + 5, boxY + 14);

		doc.setTextColor(0, 0, 0);
		doc.setFontSize(defaultFontSize);
		plan.items.forEach((item, index) => {
			doc.text(item, marginLeft + 5, boxY + headerHeight + 8 + index * itemLineHeight);
		});

		const priceX = marginLeft + 115;
		const priceBaseY = boxY + headerHeight + Math.max(plan.items.length * itemLineHeight - 14, 10);

		doc.setFont("arial", "bold");
		doc.text(plan.precio, priceX, priceBaseY);
		doc.setFont("arial", "normal");
		doc.setFontSize(16);
		doc.text(plan.etiquetaPrecio, priceX, priceBaseY + 8);
		plan.etiquetasExtra.forEach((etiqueta, index) => {
			doc.text(etiqueta, priceX, priceBaseY + 14 + index * 6);
		});
		doc.setFontSize(defaultFontSize);

		yPos = boxY + boxHeight + 4;
	};

	const writeSignatures = ({ razon, rutEmpresa, nombre, rut }) => {
		doc.line(marginLeft, yPos, 90, yPos);
		yPos += 5;
		doc.text("Asesorias e Inversiones DN SpA", marginLeft, yPos);
		yPos += 5;
		doc.text("77.526.796-8", marginLeft, yPos);
		yPos += 5;
		doc.text("P.p Emilio Jelves", marginLeft, yPos);
		yPos += 5;
		doc.text("17.011.079-K", marginLeft, yPos);
		yPos += 20;

		doc.line(marginLeft, yPos, 90, yPos);
		yPos += 5;
		doc.text(`${razon}`, marginLeft, yPos);
		yPos += 5;
		doc.text(`${rutEmpresa}`, marginLeft, yPos);
		yPos += 5;
		doc.text(`P.p. ${nombre}`, marginLeft, yPos);
		yPos += 5;
		doc.text(`${rut}`, marginLeft, yPos);
	};

	const writeClosingSection = (title, lineEnd, paragraphs, signatureData) => {
		const titleHeight = 7;
		const paragraphsHeight = paragraphs.reduce(
			(total, paragraph) => total + measureText(paragraph),
			0
		);

		ensureKeepTogether(titleHeight + paragraphsHeight + SIGNATURES_BLOCK_HEIGHT);

		writeSectionTitle(title, lineEnd);

		paragraphs.forEach((paragraph, index) => {
			const isLast = index === paragraphs.length - 1;
			if (isLast) {
				ensureKeepTogether(measureText(paragraph) + SIGNATURES_BLOCK_HEIGHT);
			}
			writeParagraph(paragraph);
		});

		yPos += SIGNATURES_GAP_BEFORE;
		writeSignatures(signatureData);
	};

	const finish = () => {
		addFooter();
	};

	addLogo();
	pageStarted = true;

	return {
		writeMainTitle,
		writeParagraph,
		writeLine,
		writeRichLine,
		writeSectionTitle,
		renderPlanBox,
		writeClosingSection,
		writeSignatures,
		finish,
		newPage,
	};
};
